import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useI18n } from '../../i18n';
import { useDashboard } from '../../context/DashboardContext';
import {
  FaPlus,
  FaVideo,
  FaEye,
  FaCheck,
  FaTimes,
  FaQuestion,
  FaChevronDown,
  FaRegClock,
} from 'react-icons/fa';
import meetingService from '../../services/meetingService';
import { notify } from '../CustomToast';
import dotsIcon from '../../assets/dots.svg';
import pencilIcon from '../../assets/pencil.svg';
import VideoCamera from '../../assets/VideoCamera.svg';
import CalendarX from '../../assets/CalendarX.svg';
import RepeatIcon from '../../assets/Repeat.svg';
import ClockUserIcon from '../../assets/ClockUser.svg';
import './MeetingsSection.css';
import ScheduleMeetingLandingModal from './ScheduleMeetingLandingModal';
import EditMeetingLandingModal from './EditMeetingLandingModal';
import CancelMeetingModal from './CancelMeetingModal';
import RsvpModal from './RsvpModal';
import ViewMeetingModal from './ViewMeetingModal';
import ForwardMeetingModal from './ForwardMeetingModal';
import MeetingForwardRequestsModal from './MeetingForwardRequestsModal';
import ProposeNewTimeModal from './ProposeNewTimeModal';
import ProposedTimeRequestsModal from './ProposedTimeRequestsModal';
import MyTimeProposalsModal from './MyTimeProposalsModal';
import BackButton from '../Common/BackButton';
import MeetingTableSkeleton from './MeetingTableSkeleton';

const MeetingsSection = ({ onMeetingJoin, activeMeeting }) => {
  const { t } = useI18n();
  const { setActiveView, returnView, deepLink, setDeepLink } = useDashboard();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const tableContainerRef = useRef(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [editMeeting, setEditMeeting] = useState(null);
  const [cancelMeeting, setCancelMeeting] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [rsvpMeeting, setRsvpMeeting] = useState(null);
  const [rsvpStatus, setRsvpStatus] = useState(null);
  const [isRsvping, setIsRsvping] = useState(false);
  const [viewMeeting, setViewMeeting] = useState(null);
  const [forwardMeeting, setForwardMeeting] = useState(null);
  const [forwardRequestsMeeting, setForwardRequestsMeeting] = useState(null);
  const [forwardRequestsViewMeeting, setForwardRequestsViewMeeting] =
    useState(null);
  const [isOrganizerView, setIsOrganizerView] = useState(true);
  const [proposeNewTimeMeeting, setProposeNewTimeMeeting] = useState(null);
  const [proposedTimeProposalsMeeting, setProposedTimeProposalsMeeting] =
    useState(null);
  const [myTimeProposalsMeeting, setMyTimeProposalsMeeting] = useState(null);
  const [actionDropdownMeetingId, setActionDropdownMeetingId] = useState(null);
  const actionDropdownRef = useRef(null);
  const [rsvpDropdownMeetingId, setRsvpDropdownMeetingId] = useState(null);
  const rsvpDropdownRef = useRef(null);
  const [dropdownRsvpStatus, setDropdownRsvpStatus] = useState({}); // Maps meetingId to the dropdown display value

  const currentUserId = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u?.id || null;
    } catch (e) {
      return null;
    }
  }, []);

  // Handle deep-link navigation from notifications
  useEffect(() => {
    if (!deepLink || deepLink.target_type !== 'meeting') return;
    const targetId = deepLink.target_id;
    setDeepLink(null);
    meetingService.show(targetId)
      .then((res) => setViewMeeting(res.data))
      .catch(() => notify.error(t('common.notifications.item_not_found')));
  }, [deepLink, setDeepLink, t]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(e.target)
      ) {
        setActionDropdownMeetingId(null);
      }
      if (
        rsvpDropdownRef.current &&
        !rsvpDropdownRef.current.contains(e.target)
      ) {
        setRsvpDropdownMeetingId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleActionDropdown = (e, meetingId) => {
    e.stopPropagation();
    setActionDropdownMeetingId(
      actionDropdownMeetingId === meetingId ? null : meetingId
    );
  };

  // Fetch meetings with pagination
  const fetchMeetings = async (pageNum = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      const response = await meetingService.list(pageNum, 10);
      // All responses now use CustomerResponseTrait format: { code, status, message, data }
      if (response.status === true && response.data) {
        const meetingList = response.data.meetings || [];
        const paginationData = response.data.pagination || {};

        if (append) {
          setMeetings((prev) => [...prev, ...meetingList]);
        } else {
          setMeetings(meetingList);
        }

        setPage(paginationData.current_page || pageNum);
        setHasMore(paginationData.has_more || false);
      } else {
        if (!append) setMeetings([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      if (!append) {
        notify('error', t('common.errors.fetch_failed'));
        setMeetings([]);
      }
      setHasMore(false);
    } finally {
      if (!append) setLoading(false);
      else setLoadingMore(false);
    }
  };

  // Load more meetings for infinite scroll
  const loadMoreMeetings = async () => {
    if (!hasMore || loadingMore || loading) return;
    setLoadingMore(true);
    await fetchMeetings(page + 1, true);
  };

  // Scroll handler for infinite scroll
  const handleTableScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight + 150;
    if (isNearBottom && hasMore && !loadingMore) {
      loadMoreMeetings();
    }
  };

  // Fetch meetings only once on component mount
  useEffect(() => {
    fetchMeetings(1, false);
  }, []);

  const getStatusInfo = (meeting) => {
    const now = new Date();
    const start = new Date(meeting.start_at);
    const end = new Date(meeting.end_at);

    const myMember = (meeting.members || []).find(
      (m) => String(m.id) === String(currentUserId)
    );
    const myRsvpStatus = myMember
      ? myMember.pivot?.rsvp_status || myMember.rsvp_status
      : null;

    if (
      meeting.organizer?.id !== currentUserId &&
      myRsvpStatus === 'declined'
    ) {
      return {
        label: t('pages.meetings.declined') || 'DECLINED',
        className: 'status-declined',
      };
    }

    if (meeting.status === 'cancelled') {
      return {
        label: t('pages.meetings.cancelled') || 'CANCELLED',
        className: 'status-cancelled',
      };
    }

    if (meeting.status === 'completed') {
      return {
        label: t('pages.meetings.finished') || 'FINISHED',
        className: 'status-finished',
      };
    }

    if (now >= start && now <= end) {
      return {
        label: t('pages.meetings.ongoing') || 'ONGOING',
        className: 'status-ongoing',
      };
    }

    if (now < start) {
      return {
        label: t('pages.meetings.upcoming') || 'UPCOMING',
        className: 'status-upcoming',
      };
    }

    return {
      label: t('pages.meetings.finished') || 'FINISHED',
      className: 'status-finished',
    };
  };

  const updateMeetingsList = (updatedMeeting) => {
    if (!updatedMeeting) return;
    setMeetings((prev) => {
      let found = false;
      const newList = prev.map((m) => {
        if (m.id === updatedMeeting.id) {
          found = true;
          // Deep merge the updated meeting to ensure all nested data is updated
          return {
            ...m,
            ...updatedMeeting,
            // Ensure members array is properly updated (critical for RSVP display)
            members: updatedMeeting.members || m.members,
            attachments:
              updatedMeeting.attachments !== undefined
                ? updatedMeeting.attachments
                : m.attachments,
          };
        }
        return m;
      });

      if (!found) {
        return [updatedMeeting, ...newList];
      }
      return newList;
    });
  };

  const fetchAndUpdateMeeting = async (meetingId) => {
    try {
      const res = await meetingService.show(meetingId);
      setEditMeeting(res.data);
    } catch (err) {
      console.error('Failed to load meeting details:', err);
    }
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    return date
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .replace(',', '');
  };

  const handleJoin = async (meeting) => {
    try {
      const response = await meetingService.join(meeting.parent_id || meeting.id, meeting.occurrence_date);
      const joinData = response.data || response;
      
      if (!joinData) {
        notify('error', 'Failed to get meeting token');
        return;
      }

      let meetingData = { ...meeting, source: 'scheduled' };

      if (joinData.provider === 'livekit') {
        meetingData.tokens = {
          ...joinData,
          provider: 'livekit',
          serverUrl: joinData.serverUrl,
          token: joinData.token,
          roomName: joinData.roomName,
        };
      } else if (joinData.agora_token || joinData.provider === 'agora') {
        meetingData.tokens = {
          ...joinData,
          provider: 'agora',
          appId: joinData.agora_app_id || joinData.appId,
          channel: joinData.agora_channel || joinData.channel,
          rtcToken: joinData.agora_token || joinData.rtcToken,
          uid: joinData.uid || null,
        };
      } else {
        console.error('Unknown token provider or missing tokens:', joinData);
        notify('error', 'Unsupported video provider response');
        return;
      }

      if (onMeetingJoin) {
        onMeetingJoin(meetingData);
      }
    } catch (error) {
      console.error('Error joining meeting:', error);
      notify('error', error.message || t('calendar.errors.join_failed'));
    }
  };

  const canJoinMeeting = (meeting) => {
    if (meeting.status === 'cancelled') return false;
    const now = new Date();
    const end = new Date(meeting.end_at);
    if (now > end) return false;

    const isOrganizer = String(meeting.organizer?.id) === String(currentUserId);
    if (isOrganizer) return true;

    const myMember = (meeting.members || []).find(
      (m) => String(m.id) === String(currentUserId)
    );
    if (!myMember) return false;

    const rsvpStatus = myMember.pivot?.rsvp_status || myMember.rsvp_status;
    if (rsvpStatus !== 'accepted' && rsvpStatus !== 'tentative') return false;

    return true;
  };

  // The open meeting window was started from this row (same meeting and occurrence)
  const isActiveMeeting = (meeting) =>
    activeMeeting?.source === 'scheduled' &&
    String(activeMeeting.id) === String(meeting.id) &&
    (activeMeeting.occurrence_date || null) === (meeting.occurrence_date || null);

  const handleCancelMeeting = async (reason = null) => {
    if (!cancelMeeting) return;
    setIsCancelling(true);
    try {
      const response = await meetingService.cancel(cancelMeeting.id, reason);
      if (response.status === true) {
        notify('success', response.message || 'Meeting cancelled');
        setCancelMeeting(null);
        if (response.data) {
          updateMeetingsList(response.data);
        } else {
          fetchMeetings();
        }
      } else {
        notify('error', response.message || 'Failed to cancel meeting');
      }
    } catch (err) {
      notify('error', err.message || 'Failed to cancel meeting');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRsvpClick = (meeting, status) => {
    setRsvpMeeting(meeting);
    setRsvpStatus(status);
  };

  const handleRsvpConfirm = async (status) => {
    if (!rsvpMeeting) return;
    const meetingId = rsvpMeeting.id;
    setIsRsvping(true);
    try {
      const response = await meetingService.rsvp(meetingId, status);
      if (response.status === true) {
        notify('success', response.message || 'RSVP updated');

        // Just set the dropdown to show the new rsvp_status from the response
        const newRsvpStatus = response.data?.rsvp_status;
        if (newRsvpStatus) {
          //   console.log('RESPONSE BEFORE', newRsvpStatus, dropdownRsvpStatus);

          setDropdownRsvpStatus((prev) => ({
            ...prev,
            [meetingId]: newRsvpStatus,
          }));
          //   console.log('RESPONSE END', newRsvpStatus, dropdownRsvpStatus);
        }

        // Close modal and dropdown
        setRsvpMeeting(null);
        setRsvpStatus(null);
        setRsvpDropdownMeetingId(null);
        fetchMeetings();
      } else {
        notify('error', response.message || 'Failed to update RSVP');
      }
    } catch (err) {
      notify('error', err.message || 'Failed to update RSVP');
    } finally {
      setIsRsvping(false);
    }
  };

  return (
    <div className="meetings-container">
      <div className="meetings-header-top">
        <h1
          className="meetings-title-main"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            margin: 0,
          }}
        >
          <BackButton
            onClick={() => setActiveView(returnView)}
            ariaLabel={t('navigation.back_to_dashboard')}
          />
          <span>{t('pages.meetings.title') || 'Meetings'}</span>
        </h1>
        {meetings.length > 0 && !loading && (
          <button
            className="schedule-btn-action"
            onClick={() => setIsScheduleModalOpen(true)}
          >
            <FaPlus />
            <span>{t('pages.calendar.schedule_meeting')}</span>
          </button>
        )}
      </div>

      <div className="meetings-table-box">
        {loading && meetings.length === 0 ? (
          <MeetingTableSkeleton rowCount={10} />
        ) : meetings.length > 0 ? (
          <>
            <div
              className="meeting-table-container"
              ref={tableContainerRef}
              onScroll={handleTableScroll}
            >
              <table className="meetings-main-table">
                <thead>
                  <tr>
                    <th className="th-title-header">
                      {t('pages.meetings.title_label') || 'Title'}
                    </th>
                    <th className="th-host-header">
                      {t('pages.meetings.host')}
                    </th>
                    <th className="th-date-header">
                      {t('pages.meetings.date_time')}
                    </th>
                    <th className="th-status-header">
                      {t('pages.meetings.status')}
                    </th>
                    <th className="th-action-header">
                      {t('pages.meetings.action')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((meeting, index) => {
                    const { label, className } = getStatusInfo(meeting);
                    const isJoined = isActiveMeeting(meeting);
                    const canJoin =
                      meeting.is_online && !isJoined && canJoinMeeting(meeting);
                    return (
                      <tr
                        key={`${meeting.id}-${meeting.occurrence_date || index}`}
                        className={index % 2 === 1 ? 'odd' : ''}
                      >
                        <td className="td-column-title">
                          <div className="title-wrapper-cell">
                            <span className="text-title-val">
                              {meeting.title}
                            </span>
                            <div className="title-actions-group">
                              {meeting.is_online && (
                                <button
                                  className={`btn-inline-join-new ${canJoin ? 'active-join' : 'disabled-join'}`}
                                  disabled={!canJoin}
                                  onClick={() => handleJoin(meeting)}
                                >
                                  <span className="icon-camera-vid"></span>
                                  <span className="join-label-text">
                                    {isJoined
                                      ? t('pages.meetings.joined') || 'Joined'
                                      : t('pages.meetings.join') || 'Join'}
                                  </span>
                                </button>
                              )}
                              {meeting.organizer?.id !== currentUserId &&
                                (() => {
                                  const myMember = (meeting.members || []).find(
                                    (m) =>
                                      String(m.id) === String(currentUserId)
                                  );
                                  if (!myMember) return null;
                                  // Use dropdown status if set, otherwise use member data
                                  const myRsvp =
                                    dropdownRsvpStatus[meeting.id] ||
                                    myMember.rsvp_status;
                                  const rsvpLabelKey = myRsvp
                                    ? `pages.meetings.${myRsvp}`
                                    : 'pages.meetings.rsvp';
                                  const rsvpLabel =
                                    t(rsvpLabelKey) ||
                                    (myRsvp
                                      ? myRsvp.charAt(0).toUpperCase() +
                                        myRsvp.slice(1)
                                      : 'RSVP');

                                  return (
                                    <div
                                      className="rsvp-dropdown-container"
                                      ref={
                                        rsvpDropdownMeetingId === meeting.id
                                          ? rsvpDropdownRef
                                          : null
                                      }
                                    >
                                      <button
                                        className={`rsvp-dropdown-btn ${myRsvp ? 'has-rsvp' : 'no-rsvp'}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setRsvpDropdownMeetingId(
                                            rsvpDropdownMeetingId === meeting.id
                                              ? null
                                              : meeting.id
                                          );
                                        }}
                                      >
                                        <span>{rsvpLabel}</span>
                                        <FaChevronDown className="chevron-icon" />
                                      </button>
                                      {rsvpDropdownMeetingId === meeting.id && (
                                        <div className="rsvp-dropdown-menu">
                                          <div
                                            className="rsvp-dropdown-item"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setRsvpDropdownMeetingId(null);
                                              handleRsvpClick(
                                                meeting,
                                                'accepted'
                                              );
                                            }}
                                          >
                                            <FaCheck className="rsvp-item-icon accept" />
                                            <span>
                                              {t('pages.meetings.accept') ||
                                                'Accept'}
                                            </span>
                                          </div>
                                          <div
                                            className="rsvp-dropdown-item"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setRsvpDropdownMeetingId(null);
                                              handleRsvpClick(
                                                meeting,
                                                'tentative'
                                              );
                                            }}
                                          >
                                            <FaQuestion className="rsvp-item-icon tentative" />
                                            <span>
                                              {t('pages.meetings.tentative') ||
                                                'Tentative'}
                                            </span>
                                          </div>
                                          <div
                                            className="rsvp-dropdown-item"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setRsvpDropdownMeetingId(null);
                                              handleRsvpClick(
                                                meeting,
                                                'declined'
                                              );
                                            }}
                                          >
                                            <FaTimes className="rsvp-item-icon decline" />
                                            <span>
                                              {t('pages.meetings.decline') ||
                                                'Decline'}
                                            </span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}
                            </div>
                          </div>
                        </td>
                        <td className="td-column-host">
                          <span className="text-host-val">
                            {meeting.organizer?.name || '-'}
                          </span>
                        </td>
                        <td className="td-column-date">
                          <span className="text-date-val">
                            {formatDateTime(meeting.start_at)}
                          </span>
                        </td>
                        <td className="td-column-status">
                          <span className={`badge-status-new ${className}`}>
                            {label}
                          </span>
                        </td>
                        <td className="td-column-action">
                          {meeting.organizer?.id === currentUserId ? (
                            <div
                              className="action-dropdown-container"
                              ref={
                                actionDropdownMeetingId === meeting.id
                                  ? actionDropdownRef
                                  : null
                              }
                            >
                              <button
                                className="action-menu-btn"
                                onClick={(e) =>
                                  toggleActionDropdown(e, meeting.id)
                                }
                                title="Actions"
                              >
                                <img
                                  src={dotsIcon}
                                  alt="Actions"
                                  className="action-dots-icon"
                                />
                              </button>
                              {actionDropdownMeetingId === meeting.id && (
                                <div className="action-dropdown-menu">
                                  <div
                                    className="action-dropdown-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setActionDropdownMeetingId(null);
                                      setViewMeeting(meeting);
                                    }}
                                  >
                                    <FaEye className="action-dropdown-icon" />
                                    <span>
                                      {t('pages.meetings.view_detail') ||
                                        'View Detail'}
                                    </span>
                                  </div>
                                  <>
                                    <div
                                      className="action-dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActionDropdownMeetingId(null);
                                        setEditMeeting(meeting);
                                        fetchAndUpdateMeeting(meeting.id);
                                      }}
                                    >
                                      <img
                                        src={pencilIcon}
                                        alt=""
                                        className="action-dropdown-icon"
                                      />
                                      <span>
                                        {t('pages.meetings.edit') || 'Edit'}
                                      </span>
                                    </div>
                                    <div
                                      className="action-dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActionDropdownMeetingId(null);
                                        setForwardRequestsMeeting(meeting);
                                        setIsOrganizerView(true);
                                      }}
                                    >
                                      <img
                                        src={RepeatIcon}
                                        alt=""
                                        className="action-dropdown-icon"
                                      />
                                      <span>
                                        {t('pages.meetings.forward_requests') ||
                                          'Forward Meeting Requests'}
                                      </span>
                                    </div>
                                    <div
                                      className="action-dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setActionDropdownMeetingId(null);
                                        setProposedTimeProposalsMeeting(
                                          meeting
                                        );
                                      }}
                                    >
                                      <img
                                        src={ClockUserIcon}
                                        alt=""
                                        className="action-dropdown-icon"
                                      />
                                      <span>
                                        {t('pages.meetings.time_proposals') ||
                                          'Time Proposals'}
                                      </span>
                                    </div>
                                    {meeting.status !== 'cancelled' && (
                                      <div
                                        className="action-dropdown-item delete-item"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setActionDropdownMeetingId(null);
                                          setCancelMeeting(meeting);
                                        }}
                                      >
                                        <img
                                          src={CalendarX}
                                          alt=""
                                          className="action-dropdown-icon"
                                        />
                                        <span>
                                          {t('pages.meetings.cancel') ||
                                            'Cancel'}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                </div>
                              )}
                            </div>
                          ) : (
                            (() => {
                              const myMember = (meeting.members || []).find(
                                (m) => String(m.id) === String(currentUserId)
                              );
                              const myRsvp =
                                myMember?.pivot?.rsvp_status ||
                                myMember?.rsvp_status;

                              if (
                                myRsvp === 'accepted' &&
                                meeting.status !== 'cancelled'
                              ) {
                                return (
                                  <div
                                    className="action-dropdown-container"
                                    ref={
                                      actionDropdownMeetingId === meeting.id
                                        ? actionDropdownRef
                                        : null
                                    }
                                  >
                                    <button
                                      className="action-menu-btn"
                                      onClick={(e) =>
                                        toggleActionDropdown(e, meeting.id)
                                      }
                                      title="Actions"
                                    >
                                      <img
                                        src={dotsIcon}
                                        alt="Actions"
                                        className="action-dots-icon"
                                      />
                                    </button>
                                    {actionDropdownMeetingId === meeting.id && (
                                      <div className="action-dropdown-menu">
                                        <div
                                          className="action-dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActionDropdownMeetingId(null);
                                            setForwardMeeting(meeting);
                                          }}
                                        >
                                          <img
                                            src={RepeatIcon}
                                            alt=""
                                            className="action-dropdown-icon"
                                          />
                                          <span>
                                            {t(
                                              'pages.meetings.forward_meeting'
                                            ) || 'Forward Meeting'}
                                          </span>
                                        </div>
                                        <div
                                          className="action-dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActionDropdownMeetingId(null);
                                            setForwardRequestsViewMeeting(
                                              meeting
                                            );
                                            setIsOrganizerView(false);
                                          }}
                                        >
                                          <img
                                            src={RepeatIcon}
                                            alt=""
                                            className="action-dropdown-icon"
                                          />
                                          <span>
                                            {t(
                                              'pages.meetings.forward_requests'
                                            ) || 'Forward Meeting Requests'}
                                          </span>
                                        </div>
                                        {meeting.status !== 'cancelled' &&
                                          new Date(meeting.end_at) >=
                                            new Date() && (
                                            <div
                                              className="action-dropdown-item"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setActionDropdownMeetingId(
                                                  null
                                                );
                                                setProposeNewTimeMeeting(
                                                  meeting
                                                );
                                              }}
                                            >
                                              <img
                                                src={ClockUserIcon}
                                                alt=""
                                                className="action-dropdown-icon"
                                              />
                                              <span>
                                                {t(
                                                  'pages.meetings.propose_new_time'
                                                ) || 'Propose a New Time'}
                                              </span>
                                            </div>
                                          )}
                                        <div
                                          className="action-dropdown-item"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setActionDropdownMeetingId(null);
                                            setMyTimeProposalsMeeting(meeting);
                                          }}
                                        >
                                          <img
                                            src={ClockUserIcon}
                                            alt=""
                                            className="action-dropdown-icon"
                                          />
                                          <span>
                                            {t(
                                              'pages.meetings.my_time_proposals'
                                            ) || 'My Time Proposals'}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return null;
                            })()
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {loadingMore && <MeetingTableSkeleton rowCount={1} />}
            </div>
          </>
        ) : (
          <div className="meetings-empty-state">
            <div className="empty-state-content">
              <div className="empty-icon-circle">
                <FaVideo className="empty-video-icon" />
              </div>
              <h2 className="empty-title-text">
                {t('pages.meetings.no_meetings_found')}
              </h2>
              <button
                className="schedule-btn-empty"
                onClick={() => setIsScheduleModalOpen(true)}
              >
                <FaPlus />
                <span>{t('pages.calendar.schedule_meeting')}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <ScheduleMeetingLandingModal
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
        onSuccess={(data) => {
          setIsScheduleModalOpen(false);
          fetchMeetings(1, false);
        }}
      />

      <EditMeetingLandingModal
        isOpen={!!editMeeting}
        meeting={editMeeting}
        onClose={() => setEditMeeting(null)}
        onSuccess={(data) => {
          setEditMeeting(null);
          fetchMeetings(1, false);
        }}
      />

      <CancelMeetingModal
        isOpen={!!cancelMeeting}
        meeting={cancelMeeting}
        onClose={() => setCancelMeeting(null)}
        onConfirm={handleCancelMeeting}
        loading={isCancelling}
      />

      <RsvpModal
        isOpen={!!rsvpMeeting}
        meeting={rsvpMeeting}
        status={rsvpStatus}
        onClose={() => {
          setRsvpMeeting(null);
          setRsvpStatus(null);
        }}
        onConfirm={handleRsvpConfirm}
        loading={isRsvping}
      />

      <ViewMeetingModal
        isOpen={!!viewMeeting}
        meeting={viewMeeting}
        currentUserId={currentUserId}
        onUpdate={updateMeetingsList}
        onClose={() => setViewMeeting(null)}
      />

      <ForwardMeetingModal
        isOpen={!!forwardMeeting}
        meeting={forwardMeeting}
        onClose={() => setForwardMeeting(null)}
      />

      <MeetingForwardRequestsModal
        isOpen={!!forwardRequestsMeeting}
        meeting={forwardRequestsMeeting}
        onClose={() => setForwardRequestsMeeting(null)}
        isOrganizerView={isOrganizerView}
      />

      <MeetingForwardRequestsModal
        isOpen={!!forwardRequestsViewMeeting}
        meeting={forwardRequestsViewMeeting}
        onClose={() => setForwardRequestsViewMeeting(null)}
        isOrganizerView={false}
      />

      <ProposeNewTimeModal
        isOpen={!!proposeNewTimeMeeting}
        meeting={proposeNewTimeMeeting}
        onClose={() => setProposeNewTimeMeeting(null)}
        onSuccess={() => {
          if (proposeNewTimeMeeting) {
            setMyTimeProposalsMeeting(proposeNewTimeMeeting);
          }
          setProposeNewTimeMeeting(null);
          fetchMeetings();
        }}
      />

      {proposedTimeProposalsMeeting && (
        <ProposedTimeRequestsModal
          meeting={proposedTimeProposalsMeeting}
          onClose={() => setProposedTimeProposalsMeeting(null)}
        />
      )}

      {myTimeProposalsMeeting && (
        <MyTimeProposalsModal
          meeting={myTimeProposalsMeeting}
          onClose={() => setMyTimeProposalsMeeting(null)}
        />
      )}
    </div>
  );
};

export default MeetingsSection;
